import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { STAGE_MAP } from './crmData'
import { SearchInput, CrmToaster, crmToast } from './crmUi'
import { metricById } from './metricsRegistry'
import { getPipelineReport, getUsers, type PipelineReport } from './crmApi'
import { DealDetail } from './DealDetail'
import { LeadRedirect } from './CrmLeadPage'

/**
 * Drill-through record list (PRD-02 §5.5) — the records behind a clicked number.
 * RECONCILES BY CONSTRUCTION: it renders the same pipelineReport rows the metric was computed
 * from, filtered identically, so the count and summed value equal the figure that was clicked.
 * Filters are visible as removable chips; header states the origin metric, count and sum;
 * sortable; CSV export; explicit empty state.
 */

const gbp = (n: number) => `£${Math.round(n).toLocaleString()}`
const stageLabel = (k: string) => STAGE_MAP[k]?.label || k.replace(/_/g, ' ')

type SortKey = 'weighted' | 'value' | 'name' | 'stage'

export const CrmRecordList: React.FC = () => {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [report, setReport] = useState<PipelineReport | null>(null)
  const [users, setUsers] = useState<any[]>([])
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<SortKey>('weighted')
  const [openDeal, setOpenDeal] = useState<any | null>(null)

  const stage = params.get('stage') || ''
  const band = params.get('band') || ''
  const owner = params.get('owner') || ''
  const source = params.get('source') || ''
  const days = params.get('days') || ''
  const metric = metricById(params.get('metric') || '')

  useEffect(() => {
    getPipelineReport({ owner: owner || undefined, source: source || undefined, days: days ? Number(days) : undefined })
      .then(setReport)
      .catch(() => crmToast('Could not load records', 'error'))
    getUsers().then(setUsers).catch(() => {})
  }, [owner, source, days])
  const userName = (id: string | null) => (id ? users.find((u) => String(u.id) === String(id))?.name || id : 'Unassigned')

  const bandStages = (b: string): string[] =>
    report?.stages.filter((s) => s.band === b).map((s) => s.stage) || []

  const rows = useMemo(() => {
    let list = report?.deals || []
    if (stage) list = list.filter((d) => d.stage === stage)
    if (band) { const ss = bandStages(band); list = list.filter((d) => ss.includes(d.stage)) }
    const needle = q.trim().toLowerCase()
    if (needle) list = list.filter((d) => `${d.name} ${d.stage}`.toLowerCase().includes(needle))
    const arr = [...list]
    if (sort === 'weighted') arr.sort((a, b) => b.weighted - a.weighted)
    if (sort === 'value') arr.sort((a, b) => b.value - a.value)
    if (sort === 'name') arr.sort((a, b) => String(a.name).localeCompare(String(b.name)))
    if (sort === 'stage') arr.sort((a, b) => a.stage.localeCompare(b.stage))
    return arr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, stage, band, q, sort])

  // Reconciliation figures — computed over the UNSEARCHED set (search is a local convenience;
  // the chips define the metric's population).
  const population = useMemo(() => {
    let list = report?.deals || []
    if (stage) list = list.filter((d) => d.stage === stage)
    if (band) { const ss = bandStages(band); list = list.filter((d) => ss.includes(d.stage)) }
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, stage, band])
  const sumGross = Math.round(population.reduce((a, d) => a + d.value, 0))
  const sumWeighted = Math.round(population.reduce((a, d) => a + d.weighted, 0))

  const chips: { key: string; label: string }[] = [
    stage && { key: 'stage', label: `Stage: ${stageLabel(stage)}` },
    band && { key: 'band', label: `Band: ${band}` },
    owner && { key: 'owner', label: `Owner: ${userName(owner)}` },
    source && { key: 'source', label: `Source: ${source}` },
    days && { key: 'days', label: `Last ${days} days` },
  ].filter(Boolean) as any
  const removeChip = (key: string) => { const p = new URLSearchParams(params); p.delete(key); setParams(p) }

  const exportCsv = () => {
    const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`
    const lines = ['Deal,Stage,Owner,Value,Value basis,Applied,Weighted']
    rows.forEach((d) => lines.push([esc(d.name), stageLabel(d.stage), esc(userName(d.ownerId)), d.value, d.valueEstimated ? 'estimated' : 'actual', `${(d.applied * 100).toFixed(1)}%`, Math.round(d.weighted)].join(',')))
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'records.csv'
    a.click()
  }

  const th = (label: string, key: SortKey) => (
    <th className="py-2 pr-3 font-medium cursor-pointer select-none hover:text-txt-primary" onClick={() => setSort(key)}>
      {label}{sort === key ? ' ↓' : ''}
    </th>
  )

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      <div className="px-6 pt-5 pb-3 bg-surface border-b border-border shrink-0 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <button onClick={() => navigate(-1)} className="text-[12px] text-brand-600 hover:underline">← Back</button>
            <h1 className="text-lg font-bold text-txt-primary">
              {population.length} record{population.length === 1 ? '' : 's'}
              {metric ? <span className="text-txt-secondary font-medium"> — {metric.displayName}</span> : ''}
              <span className="text-[12px] text-txt-tertiary font-normal"> · as at {new Date().toLocaleDateString()}</span>
            </h1>
            <p className="text-[12px] text-txt-secondary">
              Gross {gbp(sumGross)} · weighted {gbp(sumWeighted)} — these reconcile to the figure you clicked; if they don't, that's a bug, not rounding.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SearchInput value={q} onChange={setQ} placeholder="Search within these records…" className="w-60" />
            <button onClick={exportCsv} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover">⬇ CSV</button>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {chips.length === 0 && <span className="text-[11px] text-txt-tertiary">No filters — all open pipeline records</span>}
          {chips.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-brand-50 text-brand-700 border border-brand-200">
              {c.label}
              <button onClick={() => removeChip(c.key)} className="hover:text-brand-900 font-bold" title="Remove this filter">✕</button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        <div className="rounded-xl border border-border bg-surface overflow-hidden">
          {rows.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-txt-secondary">No records match.</p>
              <p className="text-[12px] text-txt-tertiary mt-1">Filters applied: {chips.length ? chips.map((c) => c.label).join(' · ') : 'none'}{q ? ` · search "${q}"` : ''}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border bg-surface-secondary [&>th]:pl-0 [&>th:first-child]:pl-4">
                  {th('Deal', 'name')}
                  {th('Stage', 'stage')}
                  <th className="py-2 pr-3 font-medium">Owner</th>
                  {th('Value', 'value')}
                  <th className="py-2 pr-3 font-medium text-right">Applied</th>
                  {th('Weighted', 'weighted')}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} onClick={() => setOpenDeal({ ...d, company: d.name, contact: '', tags: [], team: [], qual: {}, score: 0, createdDays: 0, lastActivityDays: 0, amount: d.value })} className="border-b border-border last:border-0 hover:bg-surface-hover cursor-pointer">
                    <td className="py-2 pl-4 pr-3 text-txt-primary truncate max-w-[260px]">{d.name}</td>
                    <td className="py-2 pr-3 text-txt-secondary whitespace-nowrap">{stageLabel(d.stage)}</td>
                    <td className="py-2 pr-3 text-txt-secondary whitespace-nowrap">{userName(d.ownerId)}</td>
                    <td className="py-2 pr-3 text-txt-secondary whitespace-nowrap">{gbp(d.value)}{d.valueEstimated && <span className="text-[10px] text-txt-tertiary ml-1">est.</span>}</td>
                    <td className="py-2 pr-3 text-right text-txt-secondary">{(d.applied * 100).toFixed(0)}%{d.overridden && <span className="text-[9px] font-bold uppercase text-amber-600 ml-1">ovr</span>}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-txt-primary">{gbp(d.weighted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {openDeal && <LeadRedirect deal={openDeal as any} onDone={() => setOpenDeal(null)} />}
    </div>
  )
}
